
import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow 
} from "@/components/ui/table";
import { Search, ChevronLeft, ChevronRight, FileText, Clock, CheckCircle, XCircle, AlertTriangle } from "lucide-react";

export default function AdminCollections() {
  const [collections, setCollections] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalCollections, setTotalCollections] = useState(0);
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    fetchCollections();
  }, [page, searchQuery, statusFilter]);

  async function fetchCollections() {
    setIsLoading(true);
    try {
      // Build the query
      let query = supabase
        .from('collections')
        .select(`
          *,
          users (first_name, last_name, email),
          videos (id)
        `, { count: 'exact' });
      
      // Apply search if provided
      if (searchQuery) {
        query = query.or(`collection_url.ilike.%${searchQuery}%,title.ilike.%${searchQuery}%`);
      }
      
      // Apply status filter
      if (statusFilter !== "all") {
        query = query.eq('status', statusFilter);
      }
      
      // Apply pagination
      const from = page * pageSize;
      const to = from + pageSize - 1;
      
      // Execute query
      const { data, count, error } = await query
        .order('created_at', { ascending: false })
        .range(from, to);
        
      if (error) throw error;
      
      if (data) {
        setCollections(data);
        setTotalCollections(count || 0);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    } finally {
      setIsLoading(false);
    }
  }

  function formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  // Get status badge based on collection status
  function getStatusBadge(status: string) {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
            <CheckCircle className="h-3 w-3" /> Completed
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-500">
            <XCircle className="h-3 w-3" /> Failed
          </span>
        );
      case 'processing':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-500">
            <Clock className="h-3 w-3 animate-spin" /> Processing
          </span>
        );
      case 'requested':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-500">
            <AlertTriangle className="h-3 w-3" /> Requested
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500">
            Unknown
          </span>
        );
    }
  }

  // Handle search input
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(0); // Reset to first page on new search
    fetchCollections();
  };

  // Pagination
  const totalPages = Math.ceil(totalCollections / pageSize);
  
  const nextPage = () => {
    if (page < totalPages - 1) setPage(page + 1);
  };
  
  const prevPage = () => {
    if (page > 0) setPage(page - 1);
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">Collections</h1>
          <p className="text-gray-400 mt-1">View and manage TikTok collections</p>
        </div>
        
        <form onSubmit={handleSearch} className="mt-4 md:mt-0 w-full md:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input 
              className="pl-10 pr-4 py-2 bg-[#1A1F2C] border-gray-800 w-full md:w-80 rounded-lg"
              placeholder="Search by URL or title"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button type="submit" className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 px-3">
              Search
            </Button>
          </div>
        </form>
      </div>

      {/* Filter buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button 
          variant={statusFilter === "all" ? "default" : "outline"} 
          size="sm"
          onClick={() => setStatusFilter("all")}
          className={statusFilter === "all" ? "bg-blue-600" : "border-gray-700 text-gray-300"}
        >
          All
        </Button>
        <Button 
          variant={statusFilter === "completed" ? "default" : "outline"} 
          size="sm"
          onClick={() => setStatusFilter("completed")}
          className={statusFilter === "completed" ? "bg-green-600" : "border-gray-700 text-gray-300"}
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Completed
        </Button>
        <Button 
          variant={statusFilter === "processing" ? "default" : "outline"} 
          size="sm"
          onClick={() => setStatusFilter("processing")}
          className={statusFilter === "processing" ? "bg-blue-600" : "border-gray-700 text-gray-300"}
        >
          <Clock className="h-4 w-4 mr-2" />
          Processing
        </Button>
        <Button 
          variant={statusFilter === "failed" ? "default" : "outline"} 
          size="sm"
          onClick={() => setStatusFilter("failed")}
          className={statusFilter === "failed" ? "bg-red-600" : "border-gray-700 text-gray-300"}
        >
          <XCircle className="h-4 w-4 mr-2" />
          Failed
        </Button>
        <Button 
          variant={statusFilter === "requested" ? "default" : "outline"} 
          size="sm"
          onClick={() => setStatusFilter("requested")}
          className={statusFilter === "requested" ? "bg-yellow-600" : "border-gray-700 text-gray-300"}
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Requested
        </Button>
      </div>

      <div className="bg-[#1A1F2C] border border-gray-800 rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-900">
                    <TableHead className="w-[50px] text-left">#</TableHead>
                    <TableHead>Collection</TableHead>
                    <TableHead>Videos</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Email Sent</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {collections.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        {searchQuery ? 'No collections found matching your search' : 'No collections found'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    collections.map((collection, index) => (
                      <TableRow key={collection.id} className="border-gray-800/50">
                        <TableCell>{page * pageSize + index + 1}</TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-blue-500/10 flex items-center justify-center mr-3">
                              <FileText className="h-4 w-4 text-blue-500" />
                            </div>
                            <div>
                              <div className="font-medium truncate max-w-[250px]">
                                {collection.title || collection.collection_url}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-[250px]">
                                {collection.collection_url}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-500">
                            {collection.video_count || (collection.videos?.length || 0)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {collection.users ? (
                            <div>
                              <div className="font-medium">
                                {collection.users.first_name} {collection.users.last_name}
                              </div>
                              <div className="text-xs text-gray-500">{collection.users.email}</div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Unknown User</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(collection.status)}
                        </TableCell>
                        <TableCell>
                          {collection.email_sent ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-500">
                              Sent
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-500">
                              Not Sent
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(collection.created_at)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {collections.length > 0 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
                <div className="text-sm text-gray-500">
                  Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, totalCollections)} of {totalCollections}
                </div>
                
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={prevPage} 
                    disabled={page === 0}
                    className="h-8 w-8 p-0 border-gray-700"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  
                  <span className="text-sm text-gray-400">
                    Page {page + 1} of {totalPages || 1}
                  </span>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={nextPage} 
                    disabled={page >= totalPages - 1}
                    className="h-8 w-8 p-0 border-gray-700"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
